import { AdminMembersReportPageParams } from "@/admin/insights/member-reports/AdminMembersReportPage"
import { AdminMembersReportEventsTable_PaginationFragment$key } from "@/admin/insights/member-reports/tabs/events/__generated__/AdminMembersReportEventsTable_PaginationFragment.graphql"
import { OccurrencesOrderBy } from "@/admin/insights/member-reports/tabs/events/__generated__/AdminMembersReportEventsTableExportCSVButtonMutation.graphql"
import { AdminMembersReportEventsTablePaginationQuery } from "@/admin/insights/member-reports/tabs/events/__generated__/AdminMembersReportEventsTablePaginationQuery.graphql"
import { AdminMembersReportEventsTableQuery } from "@/admin/insights/member-reports/tabs/events/__generated__/AdminMembersReportEventsTableQuery.graphql"
import { AdminMembersReportEventsTableAttendedFilterState } from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableAttendedFilter"
import { AdminMembersReportEventsTableConfirmedFilterState } from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableConfirmedFilter"
import AdminMembersReportEventsTableFilterTags from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableFilterTags"
import AdminMembersReportEventsTableRow, {
  AdminMembersReportEventsTableRowSkeleton,
} from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableRow"
import { AdminMembersReportEventsTableStatusFilterState } from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableStatusFilter"
import AdminMembersReportEventsTableToolbar, {
  AdminMembersReportEventsTableToolbarSkeleton,
} from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableToolbar"
import { AdminMembersReportEventsTableViewedRecordingFilterState } from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableViewedRecordingFilter"
import { OrderDirection } from "@/comments/__generated__/CommentsSectionContentPaginationQuery.graphql"
import { OrganizationOccurrencesDatetimeFilter } from "@/organization/occurrence/__generated__/OrganizationOccurrenceListNextPaginationQuery.graphql"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { AppFilterDropdownContentState } from "@components/dropdown/app/AppFilterDropdownContent"
import { ProductFilterDropdownContentState } from "@components/dropdown/product/ProductFilterDropdownContent"
import { DiscoTable, DiscoTableSkeletonWithHeader, DiscoText } from "@disco-ui"
import { DiscoTableToolbarStateWithSort } from "@disco-ui/table/header/search/DiscoTableSortDropdown"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import { getBooleanFromYesNoText } from "@utils/string/stringUtils"
import { useState } from "react"
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay"

export type AdminMembersReportEventsTableToolbarState = DiscoTableToolbarStateWithSort &
  ProductFilterDropdownContentState &
  AppFilterDropdownContentState &
  AdminMembersReportEventsTableStatusFilterState &
  AdminMembersReportEventsTableConfirmedFilterState & // To filter events to in a specific community event app
  AdminMembersReportEventsTableAttendedFilterState &
  AdminMembersReportEventsTableViewedRecordingFilterState

const EVENTS_PER_PAGE = 10

const headers = [
  { value: "Name" },
  { value: "Event Location" },
  { value: "Status" },
  { value: "Confirmed" },
  { value: "Attended" },
  { value: "Attendance Time" },
  { value: "Feedback" },
  { value: "Viewed Recording" },
  { value: "Watch Progress" },
]

function AdminMembersReportEventsTable(): JSX.Element | null {
  const classes = useStyles()
  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const [activePage, setActivePage] = useState(1)
  const [toolbarState, setToolbarState] =
    useState<AdminMembersReportEventsTableToolbarState>({
      allProducts: { value: "all", title: "All" }, // To filter events to all products
      products: [], // To filter events to in a specific product
      allApps: { value: "all", title: "All" }, // To filter events to all apps
      apps: [], // To filter events to in a specific community event app
      status: null, // To filter by upcoming/past
      confirmed: null, // Whether they confirmed they were going to the event
      attended: null, // Whether they actually attended the event
      viewedRecording: null, // Whether they watched the recording of the event
      sort: {
        id: "none",
        order: { field: "start_date" as OccurrencesOrderBy },
        title: "Sort",
      },
      direction: "ASC",
    })

  const response = useLazyLoadQuery<AdminMembersReportEventsTableQuery>(
    graphql`
      query AdminMembersReportEventsTableQuery(
        $first: Int
        $after: String
        $last: Int
        $before: String
        $memberOrganizationMembershipId: ID!
        $datetimeFilter: OrganizationOccurrencesDatetimeFilter
        $includedProductIds: [ID!]
        $excludeCommunityEvents: Boolean
        $memberHasRSVP: Boolean
        $memberHasAttended: Boolean
        $memberHasViewedRecording: Boolean
        $appIds: [ID!]
        $orderBy: OccurrencesOrderBy
        $direction: OrderDirection
      ) {
        ...AdminMembersReportEventsTable_PaginationFragment
          @arguments(
            first: $first
            after: $after
            last: $last
            before: $before
            memberOrganizationMembershipId: $memberOrganizationMembershipId
            datetimeFilter: $datetimeFilter
            includedProductIds: $includedProductIds
            excludeCommunityEvents: $excludeCommunityEvents
            memberHasRSVP: $memberHasRSVP
            memberHasAttended: $memberHasAttended
            memberHasViewedRecording: $memberHasViewedRecording
            appIds: $appIds
            orderBy: $orderBy
            direction: $direction
          )
      }
    `,
    {
      first: EVENTS_PER_PAGE,
      memberOrganizationMembershipId: queryParams.om!,
      includedProductIds: toolbarState.products.length
        ? toolbarState.products.map((p) => p.value)
        : undefined,
      excludeCommunityEvents: toolbarState.products.length ? true : undefined, // This is needed because 'includedProductIds' still includes community events
      appIds: toolbarState.apps.length
        ? toolbarState.apps.map((a) => a.value)
        : undefined,
      datetimeFilter:
        (toolbarState.status?.value as OrganizationOccurrencesDatetimeFilter) ||
        undefined,
      memberHasRSVP: getBooleanFromYesNoText(toolbarState.confirmed?.value),
      memberHasAttended: getBooleanFromYesNoText(toolbarState.attended?.value),
      memberHasViewedRecording: getBooleanFromYesNoText(
        toolbarState.viewedRecording?.value
      ),
      orderBy: toolbarState.sort.order.field as OccurrencesOrderBy,
      direction: toolbarState.direction as OrderDirection,
    },
    { fetchPolicy: "network-only" }
  )

  const { data, refetch } = usePaginationFragment<
    AdminMembersReportEventsTablePaginationQuery,
    AdminMembersReportEventsTable_PaginationFragment$key
  >(
    graphql`
      fragment AdminMembersReportEventsTable_PaginationFragment on Query
      @refetchable(queryName: "AdminMembersReportEventsTablePaginationQuery")
      @argumentDefinitions(
        first: { type: "Int" }
        after: { type: "String" }
        last: { type: "Int" }
        before: { type: "String" }
        memberOrganizationMembershipId: { type: "ID!" }
        datetimeFilter: { type: "OrganizationOccurrencesDatetimeFilter" }
        includedProductIds: { type: "[ID!]" }
        excludeCommunityEvents: { type: "Boolean" }
        memberHasRSVP: { type: "Boolean" }
        memberHasAttended: { type: "Boolean" }
        memberHasViewedRecording: { type: "Boolean" }
        appIds: { type: "[ID!]" }
        orderBy: { type: "OccurrencesOrderBy" }
        direction: { type: "OrderDirection" }
      ) {
        occurrences: occurrencesReport(
          first: $first
          after: $after
          last: $last
          before: $before
          memberOrganizationMembershipId: $memberOrganizationMembershipId
          datetimeFilter: $datetimeFilter
          includedProductIds: $includedProductIds
          excludeCommunityEvents: $excludeCommunityEvents
          memberHasRSVP: $memberHasRSVP
          memberHasAttended: $memberHasAttended
          memberHasViewedRecording: $memberHasViewedRecording
          appIds: $appIds
          orderBy: $orderBy
          direction: $direction
        ) @connection(key: "AdminMembersReportEventsTable_occurrences") {
          totalCount
          edges {
            cursor
            node {
              id
              ...AdminMembersReportEventsTableRowFragment
                @arguments(organizationMembershipId: $memberOrganizationMembershipId)
            }
          }
          pageInfo {
            endCursor
            startCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
    response
  )

  if (!data?.occurrences) return null

  const occurrences = Relay.connectionToArray(data.occurrences)
  const cursorsList = data.occurrences.edges.map((e) => e.cursor)

  return (
    <DiscoTable
      headerButtons={
        <div className={classes.headerButtonsContainer}>
          <DiscoText variant={"heading-xs-600"} className={classes.title}>
            {`Event Attendance`}
          </DiscoText>
          <AdminMembersReportEventsTableToolbar
            toolbarState={toolbarState}
            setToolbarState={setToolbarState}
          />
        </div>
      }
      headerFilterTags={
        (toolbarState.products.length ||
          toolbarState.apps.length ||
          toolbarState.status ||
          toolbarState.confirmed ||
          toolbarState.attended ||
          toolbarState.viewedRecording) && (
          <AdminMembersReportEventsTableFilterTags
            toolbarState={toolbarState}
            setToolbarState={
              setToolbarState as React.Dispatch<
                React.SetStateAction<Partial<AdminMembersReportEventsTableToolbarState>>
              >
            }
          />
        )
      }
      filterDeps={[toolbarState]}
      activePage={activePage}
      setActivePage={setActivePage}
      rowsPerPage={EVENTS_PER_PAGE}
      scrollable
      testid={"AdminMembersReportEventsTable"}
      header={headers}
      rows={occurrences.map((occurrence) => (
        <AdminMembersReportEventsTableRow
          key={occurrence.id}
          occurrenceKey={occurrence}
        />
      ))}
      connection={{
        cursorsList,
        totalCount: data.occurrences.totalCount,
        pageInfo: {
          endCursor: data.occurrences.pageInfo.endCursor,
          startCursor: data.occurrences.pageInfo.startCursor,
        },
      }}
      onPaginate={refetch}
    />
  )
}

const useStyles = makeUseStyles((theme) => ({
  title: { width: "100%", fontWeight: 600 },
  headerButtonsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: theme.spacing(1.5),

    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      justifyContent: "flex-start",
    },
  },
}))

function AdminMembersReportEventsTableSkeleton() {
  const classes = useStyles()
  return (
    <DiscoTableSkeletonWithHeader
      headerButtons={
        <div className={classes.headerButtonsContainer}>
          <DiscoText variant={"heading-xs-600"} className={classes.title}>
            {`Event Attendance`}
          </DiscoText>
          <AdminMembersReportEventsTableToolbarSkeleton />
        </div>
      }
      header={headers}
      rows={EVENTS_PER_PAGE}
      row={<AdminMembersReportEventsTableRowSkeleton />}
    />
  )
}

export default Relay.withSkeleton({
  component: AdminMembersReportEventsTable,
  skeleton: AdminMembersReportEventsTableSkeleton,
})
